import { useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bars, Plus, Xmark } from "@unified-checkout/design-system/icons/index";
import { PaymobLogo } from "@unified-checkout/design-system/icons/paymob_logo";
import {
  Button, Dropdown, DropdownMenu,
  DropdownButton, DropdownMenuItem
} from "@unified-checkout/design-system";
import type { IMobileNavMenu } from "@app/dashboard/types/innerLayout/mobile-nav-menu";
import type { TInnerRoute } from "@app/dashboard/types/innerLayout/nav-links";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { useNavigationStore } from "@app/dashboard/store/navigation";
import { useUserInfoStore } from "@app/dashboard/store/userInfo";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { createBtnLinks, navLinks } from "@app/dashboard/services/inner_layout/nav-links";
import { isTestLiveModeHidden } from "@app/dashboard/utils/getLiveTestSupportedRoutes";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { isNavItemHidden } from "@app/dashboard/utils/navItemsSupportedRoutes";
import { useInnerLayoutLogic } from "@app/dashboard/views/layout/InnerLayout/utils";
import { PaymobPro } from "../PaymobPro";
import { BusinessInfoBox } from "../BusinessInfoBox";
import { ModeToggler } from "../ModeToggler";
import { NavItem } from "../NavItem";
import { AccountBar } from "../AccountBar";
import { InfoBar } from "../InfoBar";
import styles from "./mobile-nav-menu.module.scss";

const MobileNavMenu = (
  {
    onTestToggleChange,
    editProfileHandler,
    logoutHandler
  }
    : IMobileNavMenu
) => {
  const { t } = useTranslation();
  const { isLive } = useTestLiveModeStore();
  const { activeLink, setActiveLink } = useNavigationStore();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    onCreateMenuOpen,
    onCreateOptionSelected,
    isWidgetsLoaded
  } = useInnerLayoutLogic();

  const { userInfo } = useUserInfoStore();
  const { logo, userName, shortName, profileType, mId, staffId } = userInfo;

  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  const closeMenuHandler = useCallback(() => {
    setIsMenuExpanded(false);
  }, []);

  const expandBtnHandler = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const logoClickHandler = () => {
    navigate(INNER_ROUTES.HOME);
  };

  const path = location.pathname;
  const isModeTogglerHidden = isTestLiveModeHidden(path);

  const create_btn_links = useMemo(() => createBtnLinks(t), [t]);

  const mobileNavLinks = useMemo(() => navLinks(t), [t]);
  const isPaymobProEligible = getFromLocalStorage(LOCAL_STORAGE_KEYS.IS_PAYMOB_PRO_ENABLED);

  const visibleNavLinks = useMemo(
    () => mobileNavLinks
      .filter(navLink => !navLink.hasSpecialAction && !isNavItemHidden(path as TInnerRoute, navLink.hiddenRoutes || []))
      .map(navLink => ({
        ...navLink,
        subItems: navLink.subLinks?.map(subLink => ({
          icon: subLink.icon,
          id: subLink.id,
          label: subLink.label,
          route: subLink.route,
          isExternalRoute: subLink.isExternalRoute,
          selectable: subLink.selectable,
          active: activeLink.sub === subLink.label,
          hiddenRoutes: subLink.hiddenRoutes
        }))
      })),
    [activeLink.sub, mobileNavLinks, path]
  );

  const specialNavLinks = useMemo(
    () => mobileNavLinks
      .filter(navLink => navLink.hasSpecialAction && !isNavItemHidden(path as TInnerRoute, navLink.hiddenRoutes || []))
      .map(navLink => ({
        ...navLink,
        subItems: navLink.subLinks?.map(subLink => ({
          icon: subLink.icon,
          id: subLink.id,
          label: subLink.label,
          route: subLink.route,
          isExternalRoute: subLink.isExternalRoute,
          selectable: subLink.selectable,
          active: false,
          specialAction: subLink.specialAction,
          hiddenRoutes: subLink.hiddenRoutes
        }))
      })),
    [mobileNavLinks, path]
  );

  const isTestLiveModeBarVisible = !isLive && !isMenuExpanded && !isModeTogglerHidden;


  return (
    <>
      <nav className={styles["mobile-nav-container"]} data-mobile-nav-expanded={isMenuExpanded}>
        <div className={styles["mobile-nav-container__mobile-nav-menu"]}>
          <div className={styles["mobile-nav-container__mobile-nav-menu__logo"]} onClick={logoClickHandler}>
            {PaymobLogo}
          </div>

          {
            !isMenuExpanded &&
            <div className={styles["mobile-nav-container__mobile-nav-menu__current-page"]}>
              {activeLink.sub || activeLink.nav}
            </div>
          }

          <div className={styles["mobile-nav-container__mobile-nav-menu__collapse-btn"]} onClick={expandBtnHandler}>
            {isMenuExpanded ? Xmark : Bars}
          </div>
        </div>

        {
          isMenuExpanded &&
          <>
            <div className={styles["mobile-nav-container__business-box"]}>
              <BusinessInfoBox />
              {
                !!create_btn_links.length &&
                <Dropdown width="full">
                  <DropdownButton
                    renderAs={<Button
                      size="md"
                      color="primary"
                      variant="contained"
                      leadingIcon={Plus}
                      text={t('innerLayout.create')}
                    />}
                  />
                  <DropdownMenu
                    width="full"
                    horizontalPlacement="end"
                    onToggleHandler={onCreateMenuOpen}
                  >
                    {create_btn_links.map((item, i: number) => (
                      <DropdownMenuItem
                        key={`mobile-create-btn-link-${i}`}
                        icon={item.icon}
                        text={item.text}
                        onClickHandler={() => onCreateOptionSelected(item)}
                      />
                    ))}
                  </DropdownMenu>
                </Dropdown>
              }
            </div>
            {isPaymobProEligible && <PaymobPro />}

            <div className={styles["mobile-nav-container__nav-links"]}>
              {
                visibleNavLinks.map((navLink, i: number) => {
                  return (
                    <NavItem
                      key={`nav-link-${i}`}
                      icon={navLink.icon}
                      id={navLink.id}
                      label={navLink.label}
                      active={activeLink.nav === navLink.label}
                      route={navLink.route}
                      subItems={navLink.subItems}
                      hasSubLinks={!!navLink?.subLinks}
                      onNavItemClick={closeMenuHandler}
                    />
                  )
                })
              }
              {
                specialNavLinks.map((navLink, i: number) => {
                  return (
                    <NavItem
                      key={`nav-link-${navLinks.length + i}`}
                      icon={navLink.icon}
                      id={navLink.id}
                      label={navLink.label}
                      active={false}
                      disabled={navLink.specialAction === 'live-chat' && !isWidgetsLoaded['live-chat']}
                      subItems={navLink.subItems}
                      hasSubLinks={!!navLink?.subLinks}
                      specialAction={navLink.specialAction}
                      selectable={navLink.selectable}
                      onNavItemClick={closeMenuHandler}
                    />
                  )
                })
              }
            </div>

            <div className={styles["mobile-nav-container__actions-box"]}>

              {!isModeTogglerHidden && <ModeToggler onTestToggleChange={onTestToggleChange} />}

              <AccountBar
                handlers={{ logout: logoutHandler, editProfile: editProfileHandler }}
                role={profileType}
                mId={mId}
                staffId={staffId}
                userName={userName || ""}
                {
                ...(
                  logo ?
                    {
                      type: 'img',
                      imgUrl: logo
                    }
                    :
                    {
                      type: 'text',
                      userShortName: shortName
                    }
                )
                }
              />
            </div>
          </>
        }
      </nav>
      {
        isTestLiveModeBarVisible &&
        <InfoBar
          status="warning"
          text={t('innerLayout.navbar.youAreinTestMode')}
          usedFor="test_mode"
        />
      }
    </>
  )
}

export default MobileNavMenu;